import { ImageUpload, UserPreferencesUpdate, UserProfileUpdate, NewUser } from 'common';
import { HttpService, HttpServiceResult, RequestParams } from './http.service';
import { UserModel } from '@ncss/models/dist/v3';
import { FormControlError } from '@/composables/use-form-control';
import { AxiosError } from 'axios';

export class UserService extends HttpService {
  updatePreferences(update: UserPreferencesUpdate) {
    return this.send<UserModel>({
      method: 'put',
      url: '/User/Preferences',
      data: update,
    });
  }

  sendForgotPasswordEmail(email: string) {
    return this.send({
      method: 'post',
      url: '/User/SendForgotPasswordEmail',
      data: { email },
    });
  }

  updatePassword(newPassword: string, confirmPassword: string, token?: string, currentPassword?: string) {
    const request: RequestParams = {
      method: 'post',
      url: '/User/ResetPassword',
      data: {
        newPassword,
        confirmPassword,
        currentPassword,
      },
    };
    if (token) {
      Object.assign(request, { headers: { Authorization: `Bearer ${token}` } });
    }
    return this.send<{ success: true; message: string; email: string } | { success: false; message: string }>(request);
  }

  updateProfile(update: UserProfileUpdate) {
    return this.send<{ isSuccess: boolean; message: string }>({
      method: 'put',
      url: '/User',
      data: update,
    });
  }

  /**
   * Delete a user by its id field. Sends an email to support.
   * @param id User id
   * @returns
   */
  requestAccountDelete(id: string, phone: string, version: string) {
    return this.send<{ isSuccess: boolean; message: string; data?: Object }>({
      method: 'delete',
      url: '/User',
      data: { id, phone, version },
    });
  }

  updatePhoto(base64: string) {
    const photo: ImageUpload = {
      base64,
      name: 'profile-photo',
      context: {
        propertyId: 'profile-photo',
      },
    };
    return this.send<{ isSuccess: true; message: string; s3url: string } | { isSuccess: false; message: string }>({
      method: 'put',
      url: '/User/Photo',
      data: { photo },
    });
  }

  async removePhoto(): Promise<HttpServiceResult> {
    try {
      await this.send({
        method: 'delete',
        url: '/User/Photo',
      });
      return {
        success: true,
        data: null,
      };
    } catch (e) {
      return {
        success: false,
        error: 'Failed to remove profile photo',
      };
    }
  }

  async validatePassword(
    data: {
      currentPassword?: string;
      newPassword: string;
      confirmPassword: string;
      token?: string;
    },
    signal?: AbortSignal
  ): Promise<(FormControlError & { path: string })[]> {
    try {
      const req: RequestParams = {
        method: 'post',
        url: '/User/ValidatePassword',
        data,
        signal,
      };
      if (data.token) {
        Object.assign(req, { headers: { Authorization: data.token } });
      }
      await this.send<{ isSuccess: boolean; message: string }>(req);
    } catch (e) {
      if (e instanceof AxiosError && e?.response?.data?.issues) {
        return e.response.data.issues.map((i: any) => ({ path: i.path[0], msg: i.reason }));
      }
    }
    return [];
  }

  async validateUsernameOrEmail(
    emailOrUsername: string,
    isUsername: boolean = false,
    signal?: AbortSignal
  ): Promise<(FormControlError & { path: string })[]> {
    try {
      const req: RequestParams = {
        method: 'post',
        url: '/User/ValidateUsernameOrEmail',
        data: { emailOrUsername, isUsername },
        signal,
      };
      await this.send<{ isSuccess: boolean; message: string }>(req);
    } catch (e) {
      if (e instanceof AxiosError && e?.response?.data?.issues) {
        return e.response.data.issues.map((i: any) => ({ path: i.path[0], msg: i.reason }));
      }
    }
    return [];
  }

  createAccount(user: NewUser) {
    return this.send({
      method: 'post',
      url: '/User/CreateAccount',
      data: user,
    });
  }

  activateAccount(token: string) {
    return this.send({
      method: 'post',
      headers: { Authorization: token },
      url: '/User/Activate',
    });
  }

  async resendActivationEmail(emailOrUsername: string) {
    try {
      const response = await this.send<{ message: string; success: boolean }>({
        method: 'post',
        url: '/User/ResendActivation',
        data: { emailOrUsername },
      });
      return response;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.data?.message) {
        return { message: e.response.data.message, success: false };
      } else {
        throw e;
      }
    }
  }
}
